import React from 'react'
import { cm } from '~/utils/utils'

export type CustomCardVariant = 'dashed' | 'solid'

export interface CustomCardProps {
  icon?: React.ReactNode
  title: React.ReactNode
  subtitle?: React.ReactNode
  variant?: CustomCardVariant
  onClick?: () => void
  disabled?: boolean
  className?: string
  action?: React.ReactNode
}

export const truncateFileName = (fileName: string | undefined, maxWidth = 286) => {
  if (!fileName) return ''
  // Create a temporary element to measure text width
  const canvas = document.createElement('canvas')
  const ctx = canvas.getContext('2d')
  if (!ctx) return fileName

  // Set font to match the component's text style
  ctx.font =
    "14px 'Geist', ui-sans-serif, -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif"

  // If text fits within maxWidth, return as is
  if (ctx.measureText(fileName).width <= maxWidth) {
    return fileName
  }

  // Split filename and extension
  const lastDotIndex = fileName.lastIndexOf('.')
  const name = lastDotIndex > 0 ? fileName.substring(0, lastDotIndex) : fileName
  const extension = lastDotIndex > 0 ? fileName.substring(lastDotIndex) : ''

  // If name is too short to truncate meaningfully, return original
  if (name.length <= 4) {
    return fileName
  }

  // Get last 2 characters of the name (before extension)
  const lastTwoChars = name.slice(-2)
  const ellipsis = '...'
  const suffix = lastTwoChars + extension

  // Binary search to find the optimal prefix length
  let left = 1
  let right = name.length - 3 // Leave space for last 2 chars + ellipsis
  let bestPrefix = name.substring(0, 1)

  while (left <= right) {
    const mid = Math.floor((left + right) / 2)
    const prefix = name.substring(0, mid)
    const testString = prefix + ellipsis + suffix

    if (ctx.measureText(testString).width <= maxWidth) {
      bestPrefix = prefix
      left = mid + 1
    } else {
      right = mid - 1
    }
  }

  return bestPrefix + ellipsis + suffix
}

export const CustomCard: React.FC<CustomCardProps> = ({
  icon,
  title,
  subtitle,
  variant = 'dashed',
  onClick,
  disabled = false,
  className,
  action
}) => {
  const containerClasses = cm(
    'flex items-center gap-4 p-4 rounded-[16px] border border-[#EAEAEA] bg-[rgba(189,189,189,0.08)] transition-colors',
    'min-h-[72px]',
    variant === 'dashed' ? 'border-dashed' : 'border-solid',
    onClick && !disabled && 'cursor-pointer hover:bg-[rgba(189,189,189,0.12)]',
    disabled && 'opacity-50 cursor-not-allowed',
    className
  )

  return (
    <div className={containerClasses} onClick={disabled ? undefined : onClick}>
      {icon && <div className='w-6 h-6 flex items-center justify-center flex-shrink-0'>{icon}</div>}
      <div className='flex-1 min-w-0'>
        <div className='text-base leading-[20px] text-[rgba(0,0,0,0.88)] break-words'>{title}</div>
        {subtitle && (
          <div className='text-sm text-black/45 leading-[22px] break-words'>{subtitle}</div>
        )}
      </div>
      {action && <div className='flex-shrink-0'>{action}</div>}
    </div>
  )
}
